import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';

import { CalendarWeekDayEvent } from '../../models/calendar-weekday-event.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CalendarOptions } from '../../models/calendar-options.model';
import { CalendarClickEvent, CalendarClickEventType } from '../../models/calendar-click-event.model';


type MonthCell = {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
  key: string; // yyyy-mm-dd
};

@Component({
  selector: 'app-calendar-month-view',
  templateUrl: './calendar-month-view.component.html',
  styleUrls: ['./calendar-month-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarMonthViewComponent implements OnChanges {
  @Input() events: CalendarWeekDayEvent[] = [];
  @Input() options: CalendarOptions;

  @Output() eventClick = new EventEmitter<CalendarClickEvent>();

  weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Month being displayed (first day of month)
  private viewMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

  // 42 cells (6 rows x 7 cols)
  cells: MonthCell[] = [];

  // Map yyyy-mm-dd -> events
  private eventsByDay = new Map<string, CalendarWeekDayEvent[]>();

  // UI
  maxVisibleEvents = 3;

  hoverBubbleHtml: SafeHtml;


  constructor(private sanitizer: DomSanitizer) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.applyOptions();
    }

    if (changes['events']) {
      this.indexEvents();
      // no need to rebuild cells for event changes
    }
  }

  sanitizeBubble(html: string): SafeHtml {
    // Prefer sanitizing rather than bypassing. Angular sanitizes [innerHTML] by default,
    // but this makes it explicit and allows caching if you want later.
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  trackByCell = (_: number, cell: MonthCell) => cell.key;

  getEventsForCell(cell: MonthCell): CalendarWeekDayEvent[] {
    return this.eventsByDay.get(cell.key) ?? [];
  }

  getVisibleEvents(cell: MonthCell): CalendarWeekDayEvent[] {
    return this.getEventsForCell(cell).slice(0, this.maxVisibleEvents);
  }

  getHiddenCount(cell: MonthCell): number {
    const count = this.getEventsForCell(cell).length - this.maxVisibleEvents;
    return count > 0 ? count : 0;
  }

  // Day click hook (optional). Keep if your parent uses it; otherwise delete.
  openEventDialog(date: Date): void {
    // intentionally empty in this month view unless you already use it
    // (keep the method so existing template bindings don't break)
    console.log('dayClick', date);
  }

  onEventClick(e: CalendarWeekDayEvent, mouseEvent: MouseEvent): void {
    mouseEvent.stopPropagation();

    this.fireEvent(CalendarClickEventType.click, mouseEvent, e, null);

    // // allow consumer to cancel default behavior (if they want)
    // let prevented = false;
    // const preventDefault = () => { prevented = true; };

    // const payload: CalendarClickEvent = {
    //   eventType: CalendarClickEventType.click,
    //   eventData: e,
    //   mouseEvent,

    //   // keep both “fullEvent” and explicit start/end for compatibility
    //   fullEvent: e,
    //   start: e.start,
    //   end: e.end,

    //   preventDefault
    // };

    // this.eventClick.emit(payload);

    // // If you want to do default behavior here when not prevented,
    // // you can add it later:
    // // if (!prevented) { ... }
    // void prevented;
  }

  onDayClick(cell: MonthCell, mouseEvent: MouseEvent): void {
    // this.fireEvent(CalendarClickEventType.timeRangeSelected, mouseEvent, null, cell.date.toISOString(), cell.date.toISOString());
  }

  private fireEvent(eventType: CalendarClickEventType, mouseEvent: MouseEvent, e?: CalendarWeekDayEvent, start?:any, end?:any) {
    //console.log(`Calendar Event - ${name}`, args);

    // allow consumer to cancel default behavior (if they want)
    let prevented = false;
    const preventDefault = () => { prevented = true; };

    this.eventClick.emit({
      eventType: eventType,
      eventData: e?.data,
      mouseEvent: mouseEvent,
      fullEvent: mouseEvent,
      preventDefault: mouseEvent.preventDefault,
      start: start,
      end: end
    });
  }

  onMoreClick(cell: MonthCell, mouseEvent: MouseEvent): void {
    mouseEvent.stopPropagation();
    // Up to you: open a day dialog, show a popover, or switch to day view
    console.log('more', cell.date, this.getEventsForCell(cell));
  }

  // ---------- internal ----------
  private applyOptions(): void {
    const start = this.parseDate(this.options?.startDate);

    // if invalid or missing, keep existing month
    if (!start) {
      this.rebuild();
      return;
    }

    // normalize to first of month
    this.viewMonth = new Date(start.getFullYear(), start.getMonth(), 1);
    this.rebuild();
  }

  private rebuild(): void {
    this.buildCells();
    this.indexEvents();
  }

  private buildCells(): void {
    const year = this.viewMonth.getFullYear();
    const month = this.viewMonth.getMonth();

    const firstOfMonth = new Date(year, month, 1);

    // grid starts Sunday
    const gridStart = this.startOfWeek(firstOfMonth);

    const todayKey = this.toKey(new Date());

    const cells: MonthCell[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);

      const key = this.toKey(d);
      const inMonth = d.getFullYear() === year && d.getMonth() === month;

      cells.push({
        date: d,
        inMonth,
        isToday: key === todayKey,
        key
      });
    }

    this.cells = cells;
  }

  private indexEvents(): void {
    const map = new Map<string, CalendarWeekDayEvent[]>();

    for (const e of this.events ?? []) {
      const start = this.parseDate(e.start);
      if (!start) continue;

      const key = this.toKey(start);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    }

    // sort each day’s events by start
    for (const [k, list] of map.entries()) {
      list.sort((a, b) => {
        const aStart = this.parseDate(a.start);
        const bStart = this.parseDate(b.start);
        return (aStart ? +aStart : 0) - (bStart ? +bStart : 0);
      });
      map.set(k, list);
    }

    this.eventsByDay = map;
  }

  private startOfWeek(date: Date): Date {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const day = d.getDay(); // 0 = Sunday
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private toKey(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private parseDate(value: any): Date | null {
    if (!value) return null;

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
}
